/**
 * Draws an image edit (imageEdit.ts) on a canvas: the photo turned and cropped, its filter, the
 * blurred strokes, the drawing, then texts, shapes, emoji and stickers. The screen and the file
 * sent use the same drawing, at different scales.
 */
import {
  applyFilter,
  cropOf,
  displayAngle,
  fontOf,
  outputSize,
  textBox,
  textLook,
  toTurned,
  turnedSize,
  type BlurMark,
  type EditState,
  type Item,
  type Mark,
  type Quality,
  type Rect,
  type Rotation,
  type TextItem,
} from "./imageEdit";

export interface PhotoSource {
  image: CanvasImageSource;
  w: number;
  h: number;
}

export interface RenderOptions {
  /** Canvas pixels per source pixel. */
  scale: number;
  /** Items the screen draws itself (the text being typed). */
  skip?: string | null;
  /** The whole turned photo, as the crop tool shows it. */
  whole?: boolean;
  /** Only the photo and its filter, for the filter thumbnails. */
  photoOnly?: boolean;
  /** Loaded sticker images by URL. */
  stickers?: Map<string, CanvasImageSource>;
}

const EMOJI_FONT =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

function photoTransform(
  ctx: CanvasRenderingContext2D,
  src: PhotoSource,
  rotation: Rotation,
  crop: Rect,
  scale: number,
) {
  ctx.scale(scale, scale);
  ctx.translate(-crop.x, -crop.y);
  if (rotation === 90) {
    ctx.translate(src.h, 0);
    ctx.rotate(Math.PI / 2);
  } else if (rotation === 180) {
    ctx.translate(src.w, src.h);
    ctx.rotate(Math.PI);
  } else if (rotation === 270) {
    ctx.translate(0, src.w);
    ctx.rotate(-Math.PI / 2);
  }
}

function tracePath(ctx: CanvasRenderingContext2D, points: number[]) {
  ctx.beginPath();
  if (points.length < 2) return;
  ctx.moveTo(points[0]!, points[1]!);
  if (points.length === 2) {
    ctx.lineTo(points[0]! + 0.01, points[1]!);
    return;
  }
  // Midpoint smoothing, as a pen on paper.
  for (let i = 2; i < points.length - 2; i += 2) {
    const mx = (points[i]! + points[i + 2]!) / 2;
    const my = (points[i + 1]! + points[i + 3]!) / 2;
    ctx.quadraticCurveTo(points[i]!, points[i + 1]!, mx, my);
  }
  ctx.lineTo(points.at(-2)!, points.at(-1)!);
}

function canvasOf(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

/** The photo as it is now, made coarse (pixelate) or soft (blur), for the blur strokes. */
function obscured(base: HTMLCanvasElement, mode: BlurMark["mode"]) {
  const W = base.width,
    H = base.height;
  const block = Math.max(
    4,
    Math.round(Math.max(W, H) / (mode === "pixelate" ? 48 : 90)),
  );
  const small = canvasOf(W / block, H / block);
  const sctx = small.getContext("2d")!;
  sctx.imageSmoothingEnabled = true;
  sctx.imageSmoothingQuality = "high";
  sctx.drawImage(base, 0, 0, small.width, small.height);
  const out = canvasOf(W, H);
  const octx = out.getContext("2d")!;
  octx.imageSmoothingEnabled = mode === "blur";
  octx.imageSmoothingQuality = "high";
  octx.drawImage(small, 0, 0, W, H);
  return out;
}

function drawMarks(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  src: PhotoSource,
  s: EditState,
  crop: Rect,
  scale: number,
  marks: Mark[],
) {
  const cache = new Map<string, HTMLCanvasElement>();
  for (const mark of marks) {
    if (mark.kind === "draw") {
      ctx.save();
      photoTransform(ctx, src, s.rotation, crop, scale);
      ctx.strokeStyle = mark.color;
      ctx.lineWidth = mark.size;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      tracePath(ctx, mark.points);
      ctx.stroke();
      ctx.restore();
      continue;
    }
    // Blur: the stroke is a mask over a coarse copy of what is under it.
    let effect = cache.get(mark.mode);
    if (!effect) {
      effect = obscured(canvas, mark.mode);
      cache.set(mark.mode, effect);
    }
    const mask = canvasOf(canvas.width, canvas.height);
    const mctx = mask.getContext("2d")!;
    photoTransform(mctx, src, s.rotation, crop, scale);
    mctx.strokeStyle = "#000";
    mctx.lineWidth = mark.size;
    mctx.lineCap = "round";
    mctx.lineJoin = "round";
    tracePath(mctx, mark.points);
    mctx.stroke();
    mctx.setTransform(1, 0, 0, 1, 0, 0);
    mctx.globalCompositeOperation = "source-in";
    mctx.drawImage(effect, 0, 0);
    ctx.drawImage(mask, 0, 0);
  }
}

let measurer: CanvasRenderingContext2D | null = null;

/** Widths of each line of a text, in the text's own pixels. */
export function measureLines(
  item: Pick<TextItem, "text" | "font" | "size">,
): number[] {
  measurer ??= canvasOf(1, 1).getContext("2d");
  const font = fontOf(item.font);
  measurer!.font = `${font.weight} ${item.size}px ${font.family}`;
  return item.text.split("\n").map((l) => measurer!.measureText(l).width);
}

/** A text's box from its lines, kept on the item so it can be picked and moved. */
export function sizeText<T extends TextItem>(item: T): T {
  const box = textBox(measureLines(item), item.size);
  item.w = box.w;
  item.h = box.h;
  return item;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawItem(
  ctx: CanvasRenderingContext2D,
  item: Item,
  stickers: Map<string, CanvasImageSource> | undefined,
) {
  const { w, h } = item;
  if (item.kind === "text") {
    const font = fontOf(item.font);
    const look = textLook(item.color, item.background);
    if (look.bg) {
      ctx.fillStyle = look.bg;
      roundRect(ctx, -w / 2, -h / 2, w, h, item.size * (6 / 17));
      ctx.fill();
    }
    ctx.fillStyle = look.fg;
    ctx.font = `${font.weight} ${item.size}px ${font.family}`;
    ctx.textBaseline = "middle";
    ctx.textAlign = item.align;
    const line = item.size * (25 / 17);
    const padX = item.size * (9 / 17);
    const x =
      item.align === "left"
        ? -w / 2 + padX
        : item.align === "right"
          ? w / 2 - padX
          : 0;
    item.text.split("\n").forEach((text, i, lines) => {
      ctx.fillText(text, x, (i - (lines.length - 1) / 2) * line);
    });
    return;
  }
  if (item.kind === "emoji") {
    ctx.font = `${h}px ${EMOJI_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(item.emoji, 0, h * 0.06);
    return;
  }
  if (item.kind === "sticker") {
    const img = stickers?.get(item.url);
    if (img) ctx.drawImage(img, -w / 2, -h / 2, w, h);
    return;
  }
  ctx.lineWidth = item.width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  if (item.shape === "rect") ctx.rect(-w / 2, -h / 2, w, h);
  else if (item.shape === "circle")
    ctx.ellipse(0, 0, w / 2, Math.max(1, h / 2), 0, 0, Math.PI * 2);
  else {
    ctx.moveTo(-w / 2, 0);
    ctx.lineTo(w / 2, 0);
  }
  if (item.fill && (item.shape === "rect" || item.shape === "circle")) {
    ctx.fillStyle = item.fill;
    ctx.fill();
  }
  if (item.stroke) {
    ctx.strokeStyle = item.stroke;
    ctx.stroke();
    if (item.shape === "arrow") {
      const head = Math.max(item.width * 3.5, 10);
      ctx.beginPath();
      ctx.moveTo(w / 2 - head, -head * 0.75);
      ctx.lineTo(w / 2, 0);
      ctx.lineTo(w / 2 - head, head * 0.75);
      ctx.stroke();
    }
  }
}

/** Draws the edit on `canvas`, sized to the crop (or the whole turned photo) at `scale`. */
export function renderEdit(
  canvas: HTMLCanvasElement,
  src: PhotoSource,
  s: EditState,
  o: RenderOptions,
): void {
  const turned = turnedSize(src.w, src.h, s.rotation);
  const crop = o.whole
    ? { x: 0, y: 0, w: turned.w, h: turned.h }
    : cropOf(s, src.w, src.h);
  const W = Math.max(1, Math.round(crop.w * o.scale)),
    H = Math.max(1, Math.round(crop.h * o.scale));
  if (canvas.width !== W) canvas.width = W;
  if (canvas.height !== H) canvas.height = H;
  const ctx = canvas.getContext("2d", {
    willReadFrequently: s.filter !== "none",
  })!;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, W, H);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.save();
  photoTransform(ctx, src, s.rotation, crop, o.scale);
  ctx.drawImage(src.image, 0, 0, src.w, src.h);
  ctx.restore();
  if (s.filter !== "none") {
    const pixels = ctx.getImageData(0, 0, W, H);
    applyFilter(pixels.data, s.filter);
    ctx.putImageData(pixels, 0, 0);
  }
  if (o.photoOnly) return;
  drawMarks(ctx, canvas, src, s, crop, o.scale, s.marks);
  for (const item of s.items) {
    if (item.id === o.skip) continue;
    const c = toTurned(item, src.w, src.h, s.rotation);
    ctx.save();
    ctx.translate((c.x - crop.x) * o.scale, (c.y - crop.y) * o.scale);
    ctx.rotate((displayAngle(item, s.rotation) * Math.PI) / 180);
    ctx.scale(o.scale, o.scale);
    drawItem(ctx, item, o.stickers);
    ctx.restore();
  }
}

/** Loads every sticker an edit uses. */
export async function loadStickers(
  s: EditState,
  known = new Map<string, CanvasImageSource>(),
): Promise<Map<string, CanvasImageSource>> {
  const urls = s.items.flatMap((i) => (i.kind === "sticker" ? [i.url] : []));
  await Promise.all(
    urls
      .filter((u) => !known.has(u))
      .map(async (u) => {
        const img = new Image();
        img.src = u;
        try {
          await img.decode();
          known.set(u, img);
        } catch {
          /* a sticker that does not load is left out */
        }
      }),
  );
  return known;
}

/** The edited photo as the JPEG that is sent, at the chosen quality. */
export async function exportEdit(
  file: Blob,
  s: EditState,
  quality: Quality,
  type: "image/jpeg" | "image/png" = "image/jpeg",
): Promise<{ blob: Blob; w: number; h: number }> {
  const bitmap = await createImageBitmap(file);
  try {
    const src = { image: bitmap, w: bitmap.width, h: bitmap.height };
    const crop = cropOf(s, src.w, src.h);
    const out = outputSize(crop.w, crop.h, quality);
    const canvas = canvasOf(out.w, out.h);
    const ctx = canvas.getContext("2d")!;
    if (type === "image/jpeg") {
      // JPEG has no transparency: a PNG's clear parts go white, as WhatsApp sends them.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, out.w, out.h);
    }
    const work = canvasOf(out.w, out.h);
    renderEdit(work, src, s, {
      scale: out.scale,
      stickers: await loadStickers(s),
    });
    ctx.drawImage(work, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, type, 0.86),
    );
    if (!blob) throw new Error("export_failed");
    return { blob, w: out.w, h: out.h };
  } finally {
    bitmap.close();
  }
}
